import React from "react";
import { Link } from "react-router-dom";
import HUDFrame from "./HUDFrame";
import BudScanner from "./BudScanner";
import { Sprout, Truck, UtensilsCrossed, Boxes, ArrowUpRight } from "lucide-react";

/**
 * Platform showcase — four BudScanner reticles (the exact same eye-candy
 * component used on each of the individual feature/vertical pages) so the
 * homepage teaser mirrors the destination pages' visual style. Each card
 * links directly to its full industry page.
 */

const CARDS = [
  {
    Icon: Sprout,
    pill: "AGRICULTURE",
    title: "FIELD-GRADE CROP INTELLIGENCE",
    image:
      "https://images.unsplash.com/photo-1464226184884-fa280b87c399?auto=format&fit=crop&w=1200&q=85",
    label: "CROP ANALYSIS",
    score: "96.4%",
    stat: { label: "CROP HEALTH", value: "OPTIMAL", sub: "94.1% VIGOR" },
    to: "/industries/agriculture",
  },
  {
    Icon: Truck,
    pill: "FOOD DISTRIBUTION",
    title: "INTAKE-DOCK AUTO-GRADING",
    image:
      "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=1200&q=85",
    label: "INTAKE GRADED",
    score: "98.1%",
    stat: { label: "GRADE", value: "US #1", sub: "APPROVED" },
    to: "/industries/food-distribution",
  },
  {
    Icon: UtensilsCrossed,
    pill: "RESTAURANTS",
    title: "EVERY PASS. EVERY PLATE.",
    image:
      "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=1200&q=85",
    label: "PLATE APPROVED",
    score: "97.4%",
    stat: { label: "PORTION", value: "6.2 oz", sub: "IN-SPEC" },
    to: "/industries/restaurants",
  },
  {
    Icon: Boxes,
    pill: "SUPPLY CHAIN",
    title: "PHOTOGRAPHIC CHAIN-OF-CUSTODY",
    image:
      "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1200&q=85",
    label: "CHAIN VERIFIED",
    score: "99.2%",
    stat: { label: "HAND-OFFS", value: "4/4", sub: "VERIFIED" },
    to: "/industries/supply-chain",
  },
];

export default function PlatformShowcase() {
  return (
    <section className="px-6 pb-12" data-testid="platform-showcase">
      <div className="max-w-[1400px] mx-auto">
        <HUDFrame padding="p-7 md:p-10">
          {/* Heading */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
            <div>
              <div className="hud-pill" data-testid="showcase-pill">PLATFORM IN MOTION</div>
              <h2
                className="mt-4 font-display font-black leading-tight"
                style={{
                  fontSize: "clamp(24px, 3.4vw, 40px)",
                  letterSpacing: "0.05em",
                }}
              >
                <span style={{ color: "#ffffff" }}>ONE BRILLIANT SOFTWARE.</span>
                <br />
                <span className="text-green glow">SEVERAL USE-CASE SCENARIOS.</span>
              </h2>
            </div>
            <div className="flex items-center gap-2 text-[11px] font-display tracking-[0.28em] text-[color:var(--ss-green)]">
              <span className="ss-status-dot" /> SYSTEM ONLINE
            </div>
          </div>

          {/* 4 BudScanner mini cards — stacked until lg (1024+) to avoid
              the HUD readout overlapping the stat panel on tablets */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {CARDS.map((c) => (
              <Link
                key={c.pill}
                to={c.to}
                className="ss-showcase-tile group"
                data-testid={`showcase-tile-${c.pill.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 grid place-items-center border border-[rgba(157,252,4,0.55)] text-[color:var(--ss-green)]">
                      <c.Icon size={18} strokeWidth={1.7} />
                    </span>
                    <div>
                      <div className="text-[10px] font-display tracking-[0.28em] text-[color:var(--ss-green)]">
                        {c.pill}
                      </div>
                      <div className="font-display font-bold text-white text-sm tracking-[0.06em]">
                        {c.title}
                      </div>
                    </div>
                  </div>
                  <ArrowUpRight
                    size={20}
                    className="text-[color:var(--ss-green)] opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition"
                  />
                </div>

                {/* Scanner stacks above stat card until xl (1280+) so the
                    HUD readout never collides with the side panel */}
                <div className="grid grid-cols-1 xl:grid-cols-[1fr_180px] gap-6 xl:gap-5 xl:items-center">
                  <div className="relative pb-10">
                    <BudScanner
                      label={c.label}
                      score={c.score}
                      image={c.image}
                      alt={c.pill}
                    />
                  </div>
                  <div>
                    <HUDFrame padding="p-3">
                      <div className="text-[10px] font-display tracking-[0.28em] text-[color:var(--ss-green)]">
                        {c.stat.label}
                      </div>
                      <div className="mt-1 text-xl font-display font-black text-green glow">
                        {c.stat.value}
                      </div>
                      <div className="text-[11px] text-[color:var(--ss-muted)] mt-1 tracking-[0.22em]">
                        {c.stat.sub}
                      </div>
                    </HUDFrame>
                    <div className="mt-3 font-display text-[color:var(--ss-green)] text-[10px] tracking-[0.28em] flex items-center gap-1">
                      EXPLORE VERTICAL <ArrowUpRight size={12} />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Bottom strip */}
          <div className="mt-9 pt-5 border-t border-[rgba(157,252,4,0.18)] flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div
              className="font-display text-[color:var(--ss-green)]"
              style={{ fontSize: "12px", letterSpacing: "0.28em" }}
            >
              ONE PLATFORM · EVERY VERTICAL · FULLY AUTOMATED
            </div>
            <div
              className="font-display text-[color:var(--ss-muted)]"
              style={{ fontSize: "12.5px" }}
            >
              Looking for a custom vertical?{" "}
              <Link
                to="/contact"
                className="text-[color:var(--ss-green)] hover:opacity-80 underline decoration-[rgba(157,252,4,0.35)] underline-offset-4"
              >
                Talk to our team →
              </Link>
            </div>
          </div>
        </HUDFrame>
      </div>
    </section>
  );
}
